import { useCallback, useEffect, useRef, useState } from "react";
import { listTopics, featuredTopic, hotTopics, remainingToday, TOPIC_SORTS, TOPIC_PAGE } from "../api.js";
import { CATEGORIES } from "../categories.js";
import Icon from "../ui/Icon.jsx";
import { mark } from "../funnel.js";
import SuggestTopic from "../ui/SuggestTopic.jsx";
import AccountButton from "../ui/AccountButton.jsx";
import Booting from "./Booting.jsx";
import Broken from "./Broken.jsx";

const FRAME_LABEL = { logic: "끝까지 따지기", time: "10년 뒤" };

/**
 * 주제는 매주 10개씩 **쌓인다.** 전체를 한 번에 읽으면 몇 달 안에 느려지고
 * 읽기 비용도 붙는다 — 커서로 끊어 읽는다.
 *
 * 색은 정보를 담을 때만 쓴다. 목록을 번갈아 칠하면 뜻 없는 색종이가 되고,
 * 코랄이 「오늘의 주제」와 겹쳐 히어로의 우위까지 잡아먹는다.
 */
export default function Topics({ onPick, user, onAuthChanged, onMyDebates }) {
  const [featured, setFeatured] = useState(null);
  const [hot, setHot] = useState([]);
  const [left, setLeft] = useState(null); // null = 아직 모름
  const [sort, setSort] = useState("popular");
  const [category, setCategory] = useState(null);

  const [items, setItems] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | more | failed
  const [err, setErr] = useState(null);
  const cursor = useRef(null);
  const [done, setDone] = useState(false);

  // 정렬·분류가 바뀌면 처음부터 다시 읽는다
  const load = useCallback(async (reset) => {
    if (reset) {
      cursor.current = null;
      setDone(false);
      setItems([]);
      setState("loading");
    } else {
      setState("more");
    }
    try {
      const r = await listTopics({ sort, category, cursor: reset ? null : cursor.current });
      cursor.current = r.cursor;
      setDone(r.done);
      setItems((prev) => (reset ? r.topics : [...prev, ...r.topics]));
      setState("ready");
    } catch (e) {
      setErr(e);
      setState("failed");
    }
  }, [sort, category]);

  useEffect(() => { mark("visit"); }, []);
  useEffect(() => { featuredTopic().then(setFeatured).catch(() => {}); }, []);
  useEffect(() => { hotTopics(3).then(setHot).catch(() => {}); }, []);
  useEffect(() => { remainingToday().then(setLeft).catch(() => {}); }, [user]);
  useEffect(() => { load(true); }, [load]);

  if (state === "failed") return <Broken err={err} onRetry={() => load(true)} />;
  if (state === "loading" && !featured) return <Booting />;

  const chip = (on) =>
    on ? { background: "var(--ink)", color: "var(--paper)", minHeight: 36 } : { minHeight: 36 };

  return (
    <main className="screen">
      <header className="bar">
        <span className="logo">그래서요<span className="q">?</span></span>
        <span className="grow" />
        <AccountButton user={user} onChanged={onAuthChanged} onMyDebates={onMyDebates} />
        {/* 모르면 숫자를 말하지 않는다. 상수로 박아 두면 이미 쓴 날에도 1 이라고 거짓말한다. */}
        {left === null
          ? <span className="pill quiet">하루 한 번</span>
          : <span className={`pill ${left > 0 ? "mark" : "quiet"}`}>
              {left > 0 ? `오늘 남은 토론 ${left}` : "오늘 몫을 다 썼어요"}
            </span>}
      </header>

      <h1 className="title">뭘로 붙어볼까요?</h1>

      {featured && (
        /*
          오늘의 주제만 먹색 면에 올린다. 목록 전체가 흰 카드면 무엇이 중요한지
          안 보인다 — 하나만 뒤집어서 위계를 만든다. 붉은 테두리로 두르면
          에러처럼 읽히므로 쓰지 않는다.
        */
        <article className="card" style={{ background: "var(--ink)", color: "var(--paper)", borderColor: "var(--ink)", padding: 24, gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span className="label" style={{ color: "var(--red)" }}>오늘의 주제</span>
            <span style={{ fontSize: 12, color: "#8A8A92" }}>
              {featured.category} · {FRAME_LABEL[featured.frame] ?? FRAME_LABEL.time}
            </span>
          </div>
          <h2 className="d" style={{ fontSize: 28, lineHeight: 1.3, textWrap: "balance" }}>{featured.title}</h2>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span className="pill" style={{ borderColor: "#45454C", color: "#B9B9C0" }}>{featured.a}</span>
            <span className="pill" style={{ borderColor: "#45454C", color: "#B9B9C0" }}>{featured.b}</span>
          </div>
          <button type="button" className="btn go" style={{ alignSelf: "flex-start" }} onClick={() => onPick(featured)}>이걸로 하기</button>
        </article>
      )}

      {/*
        정렬·분류와 무관하게 늘 보인다. 최근 7일 기준이라 옛 주제가 눌러앉지 않는다.
        아무도 아직 안 붙은 날에는 통째로 안 그린다 — 전부 0 인 목록을
        「지금 많이 붙는」이라고 부르면 거짓말이 된다.
      */}
      {hot.length > 0 && (
        <section aria-labelledby="hot-title" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <h2 id="hot-title" className="label">지금 많이 붙는 주제</h2>
          <ul className="list" style={{ gap: 0 }}>
            {hot.map((t, i) => (
              <li key={t.id}>
                <button type="button" className="row-link" onClick={() => onPick(t)}>
                  <span className="num" style={{ color: "var(--red)", fontWeight: 800, minWidth: 18 }}>{i + 1}</span>
                  <span style={{ flexGrow: 1, minWidth: 0, fontWeight: 700, lineHeight: 1.45, textWrap: "pretty" }}>
                    {t.title}
                  </span>
                  <Icon name="next" size={18} />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <nav aria-label="정렬" className="scroll-x">
        {Object.entries(TOPIC_SORTS).map(([k, s]) => (
          <button key={k} type="button" aria-pressed={sort === k} onClick={() => setSort(k)}
            className="pill" style={chip(sort === k)}>
            {s.label}
          </button>
        ))}
      </nav>

      <nav aria-label="분류" className="scroll-x">
        <button type="button" aria-pressed={category === null} onClick={() => setCategory(null)}
          className="pill" style={chip(category === null)}>전체</button>
        {CATEGORIES.map((c) => (
          <button key={c} type="button" aria-pressed={category === c} onClick={() => setCategory(c)}
            className="pill" style={chip(category === c)}>{c}</button>
        ))}
      </nav>

      {state === "loading" ? (
        <ul className="list grid" aria-busy="true">
          {Array.from({ length: TOPIC_PAGE }).map((_, i) => (
            <li key={i} className="row-link" style={{ opacity: 0.45, cursor: "default" }}>
              <span className="skeleton" style={{ flexGrow: 1 }} />
            </li>
          ))}
        </ul>
      ) : items.length === 0 ? (
        <p className="note">이 분류에는 아직 주제가 없어요.</p>
      ) : (
        <ul className="list grid">
          {items.map((t) => (
            <li key={t.id}>
              <button type="button" className="row-link" onClick={() => onPick(t)}>
                <span style={{ flexGrow: 1, minWidth: 0, fontSize: 17, fontWeight: 800, lineHeight: 1.45, textWrap: "pretty" }}>
                  {t.title}
                </span>
                <Icon name="next" size={18} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {!done && items.length > 0 && (
        <button type="button" className="btn" onClick={() => load(false)} disabled={state === "more"}>
          {state === "more" ? "불러오는 중…" : "더 보기"}
        </button>
      )}

      {/*
        목록을 다 보고도 고를 게 없을 때가 신청할 마음이 드는 순간이다.
        위에 두면 토론하러 온 사람의 길을 막는다.
      */}
      <SuggestTopic />

      <footer style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <span className="note">매주 새 주제가 올라와요</span>
        <span className="note">하루 한 번 참여할 수 있어요</span>
      </footer>
    </main>
  );
}
